import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { TransactionType } from "core/models/transaction";

import api from "api";
import type { UserInfoApi } from "api/models/user";
import type { UserCardApi } from "api/models/card";
import type { ProfileEdit } from "api/models/profile";

import { useStoreUser } from "./store";


// USER
export const useQuerySearch = (name: string, stop = false) => useQuery({
	queryKey: ["account-search", name],
	queryFn: () => api.admin.user.search(name),
	enabled: !stop && Boolean(name && name.trim())
});

export const useQueryUser = (id?: number) => useQuery({
	queryKey: ["account-info", id],
	queryFn: () => api.admin.user.get(id!),
	enabled: Boolean(id)
});

export const useMutateUser = () => {
	const store = useStoreUser();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (body: { name: string; nickname?: string }) => api.admin.user.edit({ ...body, userId: store.user.id! }),
		onSuccess: (data) => {
			queryClient.setQueryData(["account-info", store.user.id], data);
			queryClient.invalidateQueries({ queryKey: ["account-search"] });
		}
	});
};

export const useMutateAccount = () => {
	const store = useStoreUser();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (body: { email?: string; phone?: string }) => api.admin.user.clear({ ...body, userId: store.user.id! }),
		onSuccess: (data) => {
			queryClient.setQueryData(["account-info", store.user.id], data);
		}
	});
};

// PROFILE
export const useMutateProfile = () => {
	const store = useStoreUser();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (body: Omit<ProfileEdit, "userId">) => api.admin.profile.edit({ ...body, userId: store.user.id! }),
		onSuccess: (data) => {
			queryClient.setQueryData(["account-info", store.user.id], (state: UserInfoApi) => ({ ...state, profiles: state.profiles?.map((p => p.id === data.id ? data : p)) }));
		}
	});
};

export const useQueryHistory = () => {
	const store = useStoreUser();
	return useQuery({
		queryKey: ["account-history", store.user.id],
		queryFn: () => api.admin.content.history({ userId: store.user.id!, page: 1, types: [TransactionType.Unlock, TransactionType.Gift] }),
		enabled: Boolean(store.user.id)
	});
};

export const useMutateHistory = () => {
	const store = useStoreUser();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ scuId }: { scuId: number }) => api.admin.content.undo(store.user.id!, scuId),
		onSuccess: (data) => {
			queryClient.setQueryData(["account-history", store.user.id], data);
			queryClient.invalidateQueries({ queryKey: ["account-info", store.user.id] });
		}
	});
};

export const useQueryDeposits = (profileId?: number) => {
	const store = useStoreUser();
	return useQuery({
		queryKey: ["account-deposits", profileId],
		queryFn: () => api.admin.profile.deposits({ profileId: profileId!, page: 1 }),
		enabled: Boolean(store.user.id && profileId)
	});
};

// CARDS
export const useQueryCards = (profileId?: number) => {
	const store = useStoreUser();
	return useQuery({
		queryKey: ["profile-cards", profileId],
		queryFn: () => api.admin.card.all(store.user.id!, profileId!),
		enabled: Boolean(store.user.id && profileId)
	});
};

export const useMutateDefaultCard = () => {
	const store = useStoreUser();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ profileId, cardId }: { profileId: number, cardId: number }) => api.admin.card.setDefault(store.user.id!, profileId, cardId),
		onSuccess: (data, { profileId }) => {
			queryClient.setQueryData(["profile-cards", profileId], data);
		}
	});
};

export const useMutateDeleteCard = () => {
	const store = useStoreUser();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ profileId, cardId }: { profileId: number, cardId: number }) => api.admin.card.delete(store.user.id!, profileId, cardId),
		onSuccess: (_, { profileId, cardId }) => {
			queryClient.setQueryData(["profile-cards", profileId], (state: UserCardApi[]) => state.filter(c => c.id != cardId));
		}
	});
};

// CAMPAIGNS
export const useQueryCampaigns = (userId?: number) => useQuery({
	queryKey: ["campaigns", userId],
	queryFn: () => api.admin.user.getCampaigns(userId!),
	enabled: Boolean(userId)
});

export const useMutateCampaign = () => {
	const store = useStoreUser();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ userId, campaignId }: { userId: number, campaignId: number }) => api.admin.user.unredeemCampaign(userId, campaignId),
		onSuccess: (data, { userId }) => {
			queryClient.setQueryData(["campaigns", userId], data);
			queryClient.invalidateQueries({ queryKey: ["account-info", store.user.id!], exact: true });
		}
	});
};